import {
  AdCampaignState,
  DateTimeRange,
  SpendStrategy,
  SponsoredListingsCampaign,
  SponsoredListingsFlight,
} from 'src/WebApiController';

export const SPEND_STRATEGY_TO_NUMBER: Record<SpendStrategy, number> = {
  [SpendStrategy.HoldForBetterPrices]: 1,
  [SpendStrategy.SteadyHarvest]: 2,
  [SpendStrategy.MeasuredPush]: 3,
  [SpendStrategy.Accelerate]: 4,
};

export const getPastFlightsForCampaign = (
  campaign: SponsoredListingsCampaign
) => {
  const currentFlight = campaign.activeFlight;
  const dateThreshold = currentFlight?.dateRange.start
    ? new Date(currentFlight.dateRange.start)
    : new Date();

  return campaign.flights
    ?.filter(
      (flight) =>
        flight.dateRange.end && new Date(flight.dateRange.end) <= dateThreshold
    )
    .toSorted((a, b) => {
      const ad = a.dateRange.end ? new Date(a.dateRange.end).getTime() : 0;
      const bd = b.dateRange.end ? new Date(b.dateRange.end).getTime() : 0;

      // Sort in descending order (most recent first)
      return bd - ad;
    });
};

export const isCampaignActive = (
  campaign: SponsoredListingsCampaign
): boolean => {
  return (
    campaign.campaignState === AdCampaignState.Active && !campaign.hasEnded
  );
};

export const filterCampaignsByDateRange = (
  campaigns: SponsoredListingsCampaign[],
  dateRange?: DateTimeRange | null
) => {
  const filterStart = dateRange?.start
    ? new Date(dateRange.start).getTime()
    : null;
  const filterEnd = dateRange?.end ? new Date(dateRange.end).getTime() : null;

  return campaigns.filter((campaign) => {
    const campaignDateRange = campaign.dateRange;
    if (!campaignDateRange.start || !campaignDateRange.end) {
      return false;
    }
    const campaignStart = new Date(campaignDateRange.start).getTime();
    const campaignEnd = new Date(campaignDateRange.end).getTime();

    // If we have filter start and campaign ends before it, exclude
    // If we have filter end and campaign starts after it, exclude
    if (
      (filterStart != null && campaignEnd < filterStart) ||
      (filterEnd != null && campaignStart > filterEnd)
    ) {
      return false;
    }

    return true;
  });
};

export const getActiveFlightsPairedWithCampaigns = (
  campaigns: SponsoredListingsCampaign[] | null
) => {
  if (campaigns == null) {
    return null;
  }

  return campaigns
    .map((c) => ({
      campaign: c,
      flights: c.flights?.filter((flight) => flight.isCurrent),
    }))
    .filter((cf) => cf.flights?.length)
    .flatMap((cf) =>
      cf.flights!.map((f) => cf.campaign && [cf.campaign, f])
    ) as [SponsoredListingsCampaign, SponsoredListingsFlight][];
};

export const getPastFlightsPairedWithCampaigns = (
  campaigns: SponsoredListingsCampaign[] | null
) => {
  if (campaigns == null) {
    return null;
  }

  return campaigns
    .map((c) => {
      const currentFlight = (c.flights ?? []).find((f) => f.isCurrent);

      const dateThreshold = currentFlight?.dateRange.start
        ? new Date(currentFlight.dateRange.start)
        : new Date();

      return {
        campaign: c,
        flights: c.flights?.filter(
          (flight) =>
            flight.dateRange.end &&
            new Date(flight.dateRange.end) <= dateThreshold
        ),
      };
    })
    .filter((cf) => cf.flights?.length)
    .flatMap((cf) =>
      cf.flights!.map((f) => cf.campaign && [cf.campaign, f])
    ) as [SponsoredListingsCampaign, SponsoredListingsFlight][];
};
